import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, AlertTriangle, Mail } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useCart, tierFor, unitPriceCents } from "@/context/CartContext";

const ORDER_EMAIL = "contact.biobalanceinno@gmail.com";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, clear } = useCart();
  // Radix Dialog (Sheet) crashes during SSR renderToString (React 19
  // Children internals). Render it only after the client has mounted.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  const orderByEmail = () => {
    const lines = items
      .map(
        (i) =>
          `${i.name} (${i.size}, ${i.sku}) — Qty ${i.qty} × $${(unitPriceCents(i.baseCents, i.qty, i.tiers) / 100).toFixed(2)} = $${((unitPriceCents(i.baseCents, i.qty, i.tiers) * i.qty) / 100).toFixed(2)}`
      )
      .join("\n");
    const subject = encodeURIComponent("Order enquiry — Lab2o research reagents");
    const body = encodeURIComponent(
      `Hello,\n\nI'd like to place an order. Here are my cart details:\n\n${lines}\n\nSubtotal: $${total.toFixed(2)}\n\nPlease reply with payment details (Zelle or ACH) and my order total.\n\nName:\nShipping address:\n\nThank you.`
    );
    window.location.href = `mailto:${ORDER_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col rounded-none" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-chivo font-extrabold tracking-tight text-2xl" data-testid="cart-drawer-title">
            Research Cart
          </SheetTitle>
          <SheetDescription className="font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-400">
            Pay by Zelle or ACH bank transfer
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 flex flex-col gap-4">
          {items.length === 0 && (
            <p className="text-sm text-slate-500 py-10 text-center" data-testid="cart-empty-message">
              Your cart is empty. Add a research vial from the catalogue.
            </p>
          )}
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-slate-200 p-4 flex gap-4"
              data-testid={`cart-item-${item.id}`}
            >
              <div className="w-16 h-16 bg-slate-50 border border-slate-100 shrink-0 overflow-hidden">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-chivo font-bold text-sm text-slate-950">{item.name}</p>
                    <p className="font-plexmono text-[10px] tracking-[0.15em] uppercase text-slate-400 mt-0.5">
                      {item.size} — {item.sku}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-slate-400 hover:text-red-600 transition-colors"
                    aria-label="Remove item"
                    data-testid={`cart-remove-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center border border-slate-200">
                    <button
                      onClick={() => updateQty(item.id, item.qty - 1)}
                      className="p-1.5 hover:bg-slate-100"
                      aria-label="Decrease quantity"
                      data-testid={`cart-qty-minus-${item.id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-plexmono text-xs" data-testid={`cart-qty-${item.id}`}>{item.qty}</span>
                    <button
                      onClick={() => updateQty(item.id, item.qty + 1)}
                      className="p-1.5 hover:bg-slate-100"
                      aria-label="Increase quantity"
                      data-testid={`cart-qty-plus-${item.id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="text-right">
                    <span className="font-chivo font-bold text-sm" data-testid={`cart-line-total-${item.id}`}>
                      ${((unitPriceCents(item.baseCents, item.qty, item.tiers) * item.qty) / 100).toFixed(2)}
                    </span>
                    {tierFor(item.qty, item.tiers) && (
                      <span className="block font-plexmono text-[9px] tracking-[0.15em] uppercase text-emerald-600" data-testid={`cart-bulk-badge-${item.id}`}>
                        Bulk −{tierFor(item.qty, item.tiers).discount_pct}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 pt-5 mt-4">
            <div className="flex items-center justify-between mb-4">
              <span className="font-plexmono text-[11px] tracking-[0.2em] uppercase text-slate-500">Subtotal</span>
              <span className="font-chivo font-black text-2xl text-slate-950" data-testid="cart-subtotal">
                ${total.toFixed(2)}
              </span>
            </div>
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={orderByEmail}
              className="w-full bg-slate-950 text-white py-4 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
              data-testid="cart-checkout-button"
            >
              <Mail className="w-3.5 h-3.5" />
              Email to place your order
            </motion.button>
            <button
              onClick={clear}
              className="w-full mt-2 py-2 font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-400 hover:text-slate-950 transition-colors"
              data-testid="cart-clear-button"
            >
              Clear cart
            </button>
            <p className="mt-3 text-slate-500 text-xs leading-snug" data-testid="cart-payment-note">
              Pay by Zelle or ACH bank transfer. Email us to place your order at{" "}
              <a href={`mailto:${ORDER_EMAIL}`} className="underline hover:text-slate-950">{ORDER_EMAIL}</a>{" "}
              and we'll reply with payment details and your order total.
            </p>
            <p className="mt-4 flex items-start gap-2 text-red-700 text-xs font-medium leading-snug" data-testid="cart-disclaimer">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              For research purposes only. Not for human or veterinary use or consumption.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
